import ExperimentWorkspaceTrackingImplementation from '@wetransfer/transfer/constant/experiments/workspace-tracking-implementation';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import { init as initAdBlockDetection } from '@wetransfer/transfer/modules/ad-block-detection';
import { load as loadAdSdkWallpaper } from '@wetransfer/transfer/modules/ad-sdk-wallpaper';
import {
  implementWorkspaceGroup,
  initAmplitude,
} from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import ErrorHandler from '@wetransfer/transfer/modules/error-handler';
import { initExperiments } from '@wetransfer/transfer/modules/experiments';
import privacy from '@wetransfer/transfer/modules/privacy';
import { init as initReferral } from '@wetransfer/transfer/modules/referral';
import { initNotifications } from '@wetransfer/transfer/modules/upload-notifications/upload-notifications';

import { init as initAnalytics } from './analytics';
import { init as initBrowser } from './browser';
import { init as initCookie } from './cookie';
import { init as initFontLoader } from './font-loader';
import { init as initFreeUser } from './free-users';
import { init as initLogs } from './logs';
import { init as initOnboarding } from './onboarding';
import { init as initPlusUsers } from './plus-users';
import { init as initPrivacy } from './privacy';
import { init as initProfitWell } from './profitWell';
import { init as initRecaptcha } from './recaptcha';
import { init as initRouting } from './routing';
import { init as initRUM } from './rum';
import { init as initSubscriptions } from './subscriptions';
import { init as initTermsAndConditions } from './terms-and-conditions';
import { init as initWTPlus } from './wt-plus';

const initWithoutMetadata = () => {
  try {
    initBrowser();
    initRecaptcha();
    initFontLoader();
    initProfitWell({
      src: null,
      scriptText: AppConfig.profitWell.snippet,
      HTMLAttributes: AppConfig.profitWell.attributes,
      async: false,
    });
    loadAdSdkWallpaper();
  } catch (error) {
    ErrorHandler.track(error);
  }
};

const initScripts = async ({
  metadata,
  userId,
  language,
  userLoggedIn,
  planTier,
  auth0UserId,
  auth,
  user,
  account,
  currentTeam,
  allTeams,
  currentStorage,
}) => {
  try {
    initLogs();
    initReferral(metadata.friendbuyMerchantId);
    initSubscriptions();
    // Because of the outages happening on March 2022. We decided to disable compatibility check for now.
    // More information can be found about the issues here: https://www.notion.so/wetransfer/TIGER-TEAM-Working-Log-cfc8c5e021474283b1f75ec08b130791
    // initCompatibilityVersion(metadata.compatibilityVersion);
    initCookie(metadata.cookieDomain);
    initTermsAndConditions();
    initAmplitude({
      apiKey: metadata.amplitudeApiKey,
      userId: auth0UserId,
      planTier,
      isInternal:
        AppConfig.amplitude?.user?.custom?.['is-wetransfer-staff'] === true,
      currentTeam,
      userEmail: user?.email,
    });
    await initExperiments({
      deploymentId: metadata.amplitudeDeploymentId,
      userId: auth0UserId,
      onInitComplete: () => {
        if (privacy.userAllowsTracking()) {
          initRUM(auth?.auth0UserId, user.planTier);
        }

        if (ExperimentWorkspaceTrackingImplementation.isWorkspaceTracking()) {
          implementWorkspaceGroup({
            memberships: allTeams,
            userEmail: user?.email,
            userPlanTier: user?.planTier,
            userStorageLimit: user?.rules?.storageLimit,
            currentTeam,
            currentStorage,
          });
        }

        initAdBlockDetection();
      },
    });
    initPrivacy({
      cookieDomain: metadata.cookieDomain,
      userId,
      onAcceptTrackers: () => initRUM(auth?.auth0UserId, user.planTier),
    });
    initAnalytics({
      cookieDomain: metadata.cookieDomain,
      userId,
      language,
      auth,
      user,
      account,
      currentTeam,
    });
    initFreeUser();
    initPlusUsers();
    initOnboarding();
    initNotifications();
    initWTPlus({ userLoggedIn, cookieDomain: metadata.cookieDomain });
  } catch (error) {
    ErrorHandler.track(error);
  }
};

export { initWithoutMetadata, initScripts, initRouting };
